import React, { useState } from "react";
import { FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { toast } from "react-toastify";
import { OrderFilters } from "../../../../types/order";
import { getDateRange } from "../../../../utils/dateUtils";
import api from "../../../../../../../../features/auth/axiosConfig";
import { Order } from "src/shared/services/interfacev2";

interface ExportButtonProps {
  orders: Order[];
  filters: OrderFilters;
  onLoading: (loading: boolean) => void;
}

const ExportButton: React.FC<ExportButtonProps> = ({ orders, filters, onLoading }) => {
  const [selectedFormat, setSelectedFormat] = useState("");

  const handleDownload = async (event: SelectChangeEvent<string>) => {
    const selectedOption = event.target.value;
    setSelectedFormat(selectedOption);

    // ✅ Don’t show toast if user just opens or re-selects “Select”
    if (!selectedOption) return;

    if (orders.length === 0) {
      toast.warning("No orders found for the selected criteria");
      return;
    }

    const { from, to } = getDateRange(filters.timeRange, filters.customStartDate, filters.customEndDate);

    const orderIds = orders.map((order) => order.orders[0].orderId).join(",");
    const staffParam = filters.selectedStaff ? `&staff_name=${encodeURIComponent(filters.selectedStaff)}` : "";

    onLoading(true);

    try {
      let endpoint = "";
      if (selectedOption === "CSV") {
        endpoint = `app/v2/import_export?export=csv&order_ids=${orderIds}&startDate=${from}&endDate=${to}${staffParam}`;
      } else if (selectedOption === "PDF") {
        endpoint = `app/v2/import_export?export=pdf&order_ids=${orderIds}&startDate=${from}&endDate=${to}${staffParam}`;
      } else {
        toast.warning("Unsupported export format selected");
        return;
      }

      const response = await api.get(endpoint);
      if (response.status === 201) {
        toast.success(`${selectedOption} email sent successfully`);
      } else {
        toast.error(`Failed to send ${selectedOption} email`);
      }
    } catch (error) {
      console.error(`Error sending ${selectedOption}:`, error);
      toast.error(`Error sending ${selectedOption} via email. Please try again.`);
    } finally {
      onLoading(false);
      // Reset back to default after success or failure
      setSelectedFormat("");
    }
  };

  return (
    <div className="relative z-[1500]">
      {" "}
      {/* ✅ keeps dropdown above overlays */}
      <FormControl fullWidth size="small" className="!rounded-lg !min-w-[150px]">
        <InputLabel id="export-label" className="!text-blue-600">
          Export Format
        </InputLabel>

        <Select
          labelId="export-label"
          label="Export Format"
          value={selectedFormat}
          onChange={handleDownload}
          style={{ borderRadius: "8px" }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 48 * 4.5,
                borderRadius: "8px",
                zIndex: 2000,
              },
            },
          }}
        >
          <MenuItem value="">Select</MenuItem>
          <MenuItem value="CSV">CSV</MenuItem>
          <MenuItem value="PDF">PDF</MenuItem>
        </Select>
      </FormControl>
    </div>
  );
};

export default ExportButton;
